import React, { FC, memo } from 'react'
import { Theme } from 'transfix-ui/core'
import { makeStyles } from 'transfix-ui/core/styles'
import { IBidRateCoachGrades, Maybe } from 'types/graphqlTypes'
import { titleCase } from 'utils/titleCase'
import { getGaugeColour, getGaugeOffset } from '.'

const RING_PATH = 'M5 42.5C7-7 80-8.5 80 42.5' as const

export interface RateCoachGaugeProps {
  score: Maybe<IBidRateCoachGrades>
  size?: 'small' | 'large'
}

const useStyles = makeStyles<Theme, RateCoachGaugeProps>({
  root: ({ size }) => ({ width: size === 'small' ? 100 : 300 }),
  text: ({ score }) => ({ fill: getGaugeColour(score) }),
  ringPath: ({ score }) => ({
    strokeDasharray: `${getGaugeOffset(score) * 1.15} 1000`,
    transition: 'stroke-dasharray 150ms cubic-bezier(0.4, 0, 0.2, 1)',
  }),
  dotPath: ({ score }) => ({
    fill: getGaugeColour(score),
    offsetDistance: `${getGaugeOffset(score)}%`,
    offsetPath: `path('${RING_PATH}')`,
    transition: 'offset-distance 150ms cubic-bezier(0.4, 0, 0.2, 1)',
  }),
})

export const RateCoachGauge: FC<RateCoachGaugeProps> = memo(({ score, size = 'small' }) => {
  const label = score ? titleCase(score) : '--'
  const classes = useStyles({ score, size })

  return (
    <svg xmlns='http://www.w3.org/2000/svg' className={classes.root} viewBox='0 0 85 50'>
      <path
        d={RING_PATH}
        fill='none'
        stroke={getGaugeColour()}
        strokeWidth='8'
        strokeLinecap='round'
      />
      {score && (
        <path
          className={classes.ringPath}
          d={RING_PATH}
          fill='none'
          stroke={getGaugeColour(score)}
          strokeWidth='8'
          strokeLinecap='round'
        />
      )}
      {score && <circle className={classes.dotPath} r='5' stroke='white' strokeWidth='2' />}
      <text
        className={classes.text}
        x='43px'
        y='35px'
        dominantBaseline='middle'
        textAnchor='middle'
        fontSize='16'
        fontWeight='bold'>
        {label}
      </text>
    </svg>
  )
})

export default RateCoachGauge
